import React, { useState, useRef, useEffect } from 'react'
import { Modal } from '../Modal'
import { Calendar, MapPin, Navigation, Globe, Clock, ChevronDown, ChevronUp } from 'lucide-react'
import { EventDetails } from '../../../types/eventDetails.types'
import { format, parseISO } from 'date-fns'
import TIMEZONES from '../../../../shared/timezones'
import { generateEventCalendarUrl } from '../../../utils/calendarUtils'

interface EventDetailsModalProps {
  event: EventDetails
  isOpen: boolean
  onClose: () => void
  primaryColor?: string
}

export const EventDetailsModal = ({
  event,
  isOpen,
  onClose,
  primaryColor = '#2563eb',
}: EventDetailsModalProps) => {
  const [showCalendarDropdown, setShowCalendarDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  // Combined date and time formatter - matches DetailsPage format
  const formatDateAndTime = () => {
    if (!event?.startsAt || !event?.endsAt) {
      return ''
    }

    const startDate = parseISO(event.startsAt)
    const endDate = parseISO(event.endsAt)

    const showDate = true // Always show date in modal
    const showTime = event.showStartTime || event.showEndTime

    // If only showing date, format like DetailsPage
    if (showDate && !showTime) {
      // Check if it's a single-day event
      if (format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')) {
        return format(startDate, 'MMMM d, yyyy')
      } else {
        // Multi-day event
        if (format(startDate, 'yyyy-MM') === format(endDate, 'yyyy-MM')) {
          // Same month and year
          return `${format(startDate, 'MMMM d')}-${format(endDate, 'd, yyyy')}`
        } else if (format(startDate, 'yyyy') === format(endDate, 'yyyy')) {
          // Same year but different months
          return `${format(startDate, 'MMMM d')} - ${format(endDate, 'MMMM d, yyyy')}`
        } else {
          // Different years
          return `${format(startDate, 'MMMM d, yyyy')} - ${format(endDate, 'MMMM d, yyyy')}`
        }
      }
    }

    // If showing both date and time, create a combined format
    if (showDate && showTime) {
      const isSameDay = format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')

      // Check if times are midnight (all-day event)
      const startTime = format(startDate, 'HH:mm')
      const endTime = format(endDate, 'HH:mm')
      const isAllDay = startTime === '00:00' && endTime === '00:00' && (!event.showStartTime || !event.showEndTime)

      if (isAllDay) {
        // Just show the date for all-day events
        if (format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')) {
          return format(startDate, 'MMMM d, yyyy')
        } else {
          // Multi-day all-day event
          if (format(startDate, 'yyyy-MM') === format(endDate, 'yyyy-MM')) {
            return `${format(startDate, 'MMMM d')}-${format(endDate, 'd, yyyy')}`
          } else {
            return `${format(startDate, 'MMMM d')} - ${format(endDate, 'MMMM d, yyyy')}`
          }
        }
      }

      if (event.showStartTime && event.showEndTime) {
        if (isSameDay) {
          // Same day: "July 5, 12:00 - 1:00 PM" or "July 5, 12:00 PM - 1:00 PM"
          const startAmPm = format(startDate, 'a')
          const endAmPm = format(endDate, 'a')

          if (startAmPm === endAmPm) {
            // Same AM/PM: "July 5, 12:00 - 1:00 PM"
            return `${format(startDate, 'MMMM d, h:mm')} - ${format(endDate, 'h:mm a')}`
          } else {
            // Different AM/PM: "July 5, 12:00 PM - 1:00 AM"
            return `${format(startDate, 'MMMM d, h:mm a')} - ${format(endDate, 'h:mm a')}`
          }
        } else {
          // Different days: "July 5, 4:00 AM - July 6, 1:00 PM"
          return `${format(startDate, 'MMMM d, h:mm a')} - ${format(endDate, 'MMMM d, h:mm a')}`
        }
      } else if (event.showStartTime) {
        // Only start time
        return `${format(startDate, 'MMMM d, yyyy')} • Starts at ${format(startDate, 'h:mm a')}`
      } else if (event.showEndTime) {
        // Only end time
        return `${format(endDate, 'MMMM d, yyyy')} • Ends at ${format(endDate, 'h:mm a')}`
      }
    }

    return ''
  }

  // Format time zone - for modal, show full name instead of abbreviation
  const formatTimeZone = () => {
    if (!event?.timeZone) return ''

    // First, check if the stored value matches an IANA timezone value
    const timezoneByValue = TIMEZONES.find(tz => tz.value === event.timeZone)
    if (timezoneByValue) {
      return timezoneByValue.label
    }

    // Check if the stored value matches a label - just return it
    const timezoneByLabel = TIMEZONES.find(tz => tz.label === event.timeZone)
    if (timezoneByLabel) {
      return timezoneByLabel.label
    }

    // If it's already a formatted string, return as is
    return event.timeZone
  }

  // Get static map URL
  const getStaticMapUrl = () => {
    if (!event.location) return null

    const { latitude, longitude, fullAddress, address1, city, state, zip } = event.location

    // If we don't have enough address info, return null
    if (!fullAddress && (!address1 || !city || !state)) return null

    const address = fullAddress || `${address1}, ${city}, ${state} ${zip}`.trim()
    const center = latitude && longitude
      ? `${latitude},${longitude}`
      : encodeURIComponent(address)

    // Using the Google Maps API key from the codebase
    return `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=14&size=600x300&markers=color:blue%7C${center}&scale=2&key=AIzaSyCkc0Aobl1liHhrfYoQwC46l-_8_5E0kcc`
  }

  // Handle calendar button click
  const handleCalendarClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowCalendarDropdown(!showCalendarDropdown)
  }

  // Handle specific calendar selection
  const handleAddToCalendar = (e: React.MouseEvent, type: 'google' | 'outlook' | 'apple' | 'outlookcom' | 'office365' | 'yahoo') => {
    e.stopPropagation()
    const url = generateEventCalendarUrl(event, type)
    if (type === 'apple' || type === 'outlook') {
      // For Apple Calendar and Outlook desktop, we need to create a temporary link to download the .ics file
      const link = document.createElement('a')
      link.href = url
      link.download = `${event.slug}.ics`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } else {
      // For web-based calendars, open in a new tab
      window.open(url, '_blank')
    }
    setShowCalendarDropdown(false)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCalendarDropdown(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  const handleGetDirections = () => {
    if (!event.location) return

    // Use directionsUrl if available, otherwise build from address
    const url = event.location.directionsUrl ||
      `https://maps.google.com?q=${encodeURIComponent(
        event.location.fullAddress ||
        `${event.location.address1}, ${event.location.city}, ${event.location.state} ${event.location.zip}`
      )}`

    window.open(url, '_blank')
  }
  const dateTime = formatDateAndTime()
  const timeZone = formatTimeZone()
  const mapUrl = getStaticMapUrl()

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">{event.name}</h2>
          {event.description && (
            <div
              className="text-gray-600 mt-1 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: event.description }}
            />
          )}
        </div>

        <div className="space-y-4">
          {/* Date & Time */}
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 mt-1" style={{ color: primaryColor || '#2563eb' }} />
            <div>
              <h3 className="font-medium">Date & Time</h3>
              <p className="text-gray-600">{dateTime}</p>
              {timeZone && (
                <p className="text-gray-600 flex items-center gap-1 mt-1">
                  <Globe className="w-4 h-4" />
                  {timeZone}
                </p>
              )}
            </div>
          </div>

          {/* Location */}
          {event.location && (
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 mt-1" style={{ color: primaryColor || '#2563eb' }} />
              <div>
                <h3 className="font-medium">Location</h3>
                {/* If we have structured address data, show it */}
                {(event.location.name || event.location.address1) ? (
                  <>
                    {event.location.name && (
                      <p className="text-gray-600">{event.location.name}</p>
                    )}
                    {event.location.address1 && (
                      <p className="text-gray-600">{event.location.address1}</p>
                    )}
                    {event.location.address2 && (
                      <p className="text-gray-600">{event.location.address2}</p>
                    )}
                    {(event.location.city || event.location.state || event.location.zip) && (
                      <p className="text-gray-600">
                        {[event.location.city, event.location.state, event.location.zip]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    )}
                  </>
                ) : (
                  /* Fallback to fullAddress if available */
                  event.location.fullAddress && (
                    <p className="text-gray-600 whitespace-pre-line">{event.location.fullAddress}</p>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        {/* Map */}
        {mapUrl && event.location && (
          <div className="relative h-[200px] rounded-lg overflow-hidden">
            <img
              src={mapUrl}
              alt={`Map showing ${event.location.name || 'event location'}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Hide the map container if image fails to load
                const target = e.target as HTMLImageElement
                target.parentElement!.style.display = 'none'
              }}
            />
            {/* Optional: Add a map marker overlay if coordinates are available */}
            {event.location.latitude && event.location.longitude && (
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                <div className="relative">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center border-4 border-white shadow-lg"
                    style={{ backgroundColor: primaryColor || '#2563eb' }}
                  >
                    <MapPin className="w-3 h-3 text-white" />
                  </div>
                  <div
                    className="absolute -inset-2 rounded-full animate-pulse opacity-20"
                    style={{ backgroundColor: primaryColor || '#2563eb' }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4">
          {event.location && (
            <button
              onClick={handleGetDirections}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: primaryColor || '#2563eb' }}
            >
              <Navigation className="w-4 h-4" />
              Get Directions
            </button>
          )}
          <div className="relative flex-1" ref={dropdownRef}>
            <button
              onClick={handleCalendarClick}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 border rounded-lg transition-colors"
              style={{
                borderColor: primaryColor,
                color: primaryColor
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${primaryColor}10`
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
            >
              <Calendar className="w-4 h-4" />
              Add to Calendar
              {showCalendarDropdown ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showCalendarDropdown && (
              <div className="absolute right-0 bottom-full mb-2 bg-white border rounded-lg shadow-lg py-1 z-50 min-w-[200px]">
                <button
                  onClick={(e) => handleAddToCalendar(e, 'apple')}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                >
                  {event.calendarIcons?.apple ? (
                    <img src={event.calendarIcons.apple} alt="Apple" className="w-4 h-4" />
                  ) : (
                    <span className="w-4 h-4 text-gray-400">🍎</span>
                  )}
                  Apple
                </button>
                <button
                  onClick={(e) => handleAddToCalendar(e, 'google')}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                >
                  {event.calendarIcons?.google ? (
                    <img src={event.calendarIcons.google} alt="Google" className="w-4 h-4" />
                  ) : (
                    <span className="w-4 h-4 text-gray-400">📅</span>
                  )}
                  Google <span className="text-gray-400 text-xs">(online)</span>
                </button>
                <button
                  onClick={(e) => handleAddToCalendar(e, 'office365')}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                >
                  {event.calendarIcons?.office365 ? (
                    <img src={event.calendarIcons.office365} alt="Office 365" className="w-4 h-4" />
                  ) : (
                    <span className="w-4 h-4 text-gray-400">📊</span>
                  )}
                  Office 365 <span className="text-gray-400 text-xs">(online)</span>
                </button>
                <button
                  onClick={(e) => handleAddToCalendar(e, 'outlook')}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                >
                  {event.calendarIcons?.outlook ? (
                    <img src={event.calendarIcons.outlook} alt="Outlook" className="w-4 h-4" />
                  ) : (
                    <span className="w-4 h-4 text-gray-400">📧</span>
                  )}
                  Outlook
                </button>
                <button
                  onClick={(e) => handleAddToCalendar(e, 'outlookcom')}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                >
                  {event.calendarIcons?.outlookcom ? (
                    <img src={event.calendarIcons.outlookcom} alt="Outlook.com" className="w-4 h-4" />
                  ) : (
                    <span className="w-4 h-4 text-gray-400">📧</span>
                  )}
                  Outlook.com <span className="text-gray-400 text-xs">(online)</span>
                </button>
                <button
                  onClick={(e) => handleAddToCalendar(e, 'yahoo')}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                >
                  {event.calendarIcons?.yahoo ? (
                    <img src={event.calendarIcons.yahoo} alt="Yahoo" className="w-4 h-4" />
                  ) : (
                    <span className="w-4 h-4 text-gray-400">📮</span>
                  )}
                  Yahoo <span className="text-gray-400 text-xs">(online)</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}
